import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { FieldArray, Formik } from 'formik';
import { useI18n } from '@whizz/react-i18n';
import type { ToggleModalProps } from '../../components/modal/types';
import type { DateRangeOptionsType, StudentType } from '../../pages/types';
import type { HandleDownloadType } from '../../forms/types';
import { Button, ChoiceGroup, Collapse, Loader } from '../../components';
import { HANDLE_DOWNLOAD_SCHEMA } from '../../forms/validations';
import SelectStudentsTableLayout from '../../pages/TopicFocus/modules/tables/SelectStudentsTableLayout';
import ConfirmButtonModal from '../buttons/ConfirmButtonModal';
import CancelButtonModal from '../buttons/CancelButtonModal';
import { GLOBAL_ICONS } from '../../util/vars';
import { DateTime } from 'luxon';
import type { TimeAssessmentFilterEnum } from '@whizz/api-model';
import { generatePDF } from '../../helper/pdfHelper';
import { ModalFooter } from '../../components/modal/Modal';
import { saveAsCsv } from '../../util/reactFunc';
import { model } from '@whizz/api-client';
import SensitiveDataToggle from '../../pages/Reports/modules/SenstiveDataToggle';
import { useLocalStorage } from 'react-use';
import { useIdentity } from '@whizz/react-identity';

export interface HandleDownloadModalProps extends Pick<ToggleModalProps, 'toggle'> {
  students: StudentType[];
  classId: number;
  className: string;
  dateInterval: DateRangeOptionsType;
  startDate: DateTime | TimeAssessmentFilterEnum;
  endDate: DateTime | null;
  currentViewRef: React.RefObject<HTMLDivElement>;
  isTableView: boolean;
}

const HandleDownloadModal = ({
  students,
  toggle,
  classId,
  className,
  dateInterval,
  startDate,
  endDate,
  isTableView,
}: HandleDownloadModalProps): JSX.Element => {

  const { locale, translate } = useI18n();
  const { me } = useIdentity();
  const [ showSensitiveData, setShowSensitiveData ] = useLocalStorage(`print_sensitive_data_${me?.username}`, true);
  const [ showIndividualReports, setShowIndividualReports ] = useState<boolean>(false);
  const [ isFileDownloading, setIsFileDownloading ] = useState<'pdf' | 'csv' | false>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const elementsIds =
      isTableView
        ?
        [ 'table-container' ]
        :
        [
          'settings-container',
          'assessment-info-container',
          'maths-age-container',
          'maths-age-improvements-container',
          'progressions-container',
          'quality-learning-time-container',
          'student-status-container',
          'progress-consistency-container',
          'topic-progress-container',
        ];

  const getParams = (studentIds: string[], showSensitiveData: boolean): {
    studentsIds: number[],
    mathsAge?: boolean,
    classAverage?: boolean,
    classChart?: boolean,
  } => {
    const ids = studentIds.map((idStr) => parseInt(idStr) || 0);

    return {
      studentsIds: ids,
      mathsAge: showSensitiveData,
      classAverage: false, // Todo: Remove
      classChart: false,  // Todo: Remove
    };
  };

  const togglePrinting = (print: boolean) => {
    const tag = document.getElementsByTagName('html')[0];
    if (print) {
      tag?.classList.add('printing');
    } else {
      tag?.classList.remove('printing');
    }
  };

  const handleCurrentView = () => {
    setIsFileDownloading('pdf');
    togglePrinting(true);
    const styleLinks: NodeListOf<HTMLLinkElement> = document.querySelectorAll('link[href^=\'https://wchat\']') ;
    const chatButton = document.getElementById('fc_frame');
    const chatFrame = document.getElementById('fc_push_frame');
    if (null != chatButton) chatButton.style.display = 'none';
    if (null != chatFrame) chatFrame.style.display = 'none'; // Prevents default div styles to be applied while generating the PDF
    styleLinks.forEach((stylesheet) => stylesheet.disabled = true);

    generatePDF('report', elementsIds).then(() => {
      if (null != chatButton) chatButton.style.display = 'flex';
      styleLinks.forEach((stylesheet) => stylesheet.disabled = false);
      setIsFileDownloading(false);
      toggle(false);
    });
  };
  const handleStudentData = () => {
    setIsFileDownloading('csv');
    model.CsvReports.groupById(classId, locale, startDate, endDate ?? DateTime.now())
      .then((data: string) => {
        saveAsCsv(data, translate('pages.REPORTS.DOWNLOAD.FILE_NAMES.class', { class: className, date: DateTime.now().toISODate() }).toUpperCase());
      })
      .finally(() => {
        setIsFileDownloading(false);
        toggle(false);
      });
  };

  const handleSubmit = (values: HandleDownloadType) => {

    setIsFileDownloading('pdf');

    const {
      studentsIds,
      mathsAge,
      classAverage,
      classChart,
    } = getParams(values.students, !!showSensitiveData);

    const urlWithParams = '../build-pdf' +
      `?class_id=${classId}` +
      `&ids=${studentsIds}` +
      `&date_interval=${dateInterval}` +
      `&start_date=${DateTime.isDateTime(startDate) ? encodeURIComponent(startDate.toISO({ format: 'basic' })) : startDate}` +
      `&end_date=${DateTime.isDateTime(endDate) ? encodeURIComponent(endDate.toISO({ format: 'basic' })) : endDate}}` +
      `&maths_age=${mathsAge}` +
      `&class_average=${classAverage}` +
      `&class_chart=${classChart}`;

    setIsFileDownloading(false);
    toggle(false);
    setTimeout(() => window.open(urlWithParams), 250);
  };

  if (isFileDownloading) {
    return (
      <Loader
        label={translate(`pages.REPORTS.DOWNLOAD.MODAL.loader_${isFileDownloading}`)}
      />
    );
  }

  return (
    <>
      <div className={'flex items-center justify-center container'} ref={modalRef}>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleCurrentView}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.current')}
        </Button>
        <Button
          icon={GLOBAL_ICONS.download}
          onClick={handleStudentData}
          isOutline
          color={'positive'}
          className={'mr-md'}
        >
          {translate('components.BUTTONS.DOWNLOAD.student')}
        </Button>
        <Button
          toggle={showIndividualReports}
          onClick={() => setShowIndividualReports(!showIndividualReports)}
          isOutline
          color={'positive'}
        >
          {translate('components.BUTTONS.DOWNLOAD.individual')}
        </Button>
      </div>
      <Collapse
        when={showIndividualReports}
      >
        <Formik
          initialValues={{
            students: [],
            settings: [],
          } as HandleDownloadType}
          validateOnMount
          validationSchema={HANDLE_DOWNLOAD_SCHEMA}
          onSubmit={handleSubmit}
        >
          {({ values, handleSubmit, setFieldValue, isValid, isSubmitting, isValidating }) => (
            <form onSubmit={handleSubmit}>
              <div className={'container'}>
                <div className={'border border-dark rounded-lg p-md'}>
                  <div className={'overflow-auto'} style={{ maxHeight: 400 }}>
                    <FieldArray
                      name={'students'}
                      render={(arrayHelpers) => (
                        <SelectStudentsTableLayout
                          id={'students'}
                          selectAllProps={{
                            indeterminate: values.students.length > 0 && values.students.length !== students.length,
                            checked: values.students.length > 0,
                            onChange: () => {
                              if (values.students.length !== students.length) {
                                students.map((student) => {
                                  if (!values.students.includes(student.id.toString())) {
                                    arrayHelpers.push(student.id.toString());
                                  }
                                });
                              } else {
                                setFieldValue('students', []);
                              }
                            },
                          }}
                        >
                          {students.map((student, index) => (
                            <div
                              key={`download-student_${student.id}`}
                              className={classnames({ 'mb-md' : index + 1 !== students.length })}
                            >
                              <ChoiceGroup
                                id={`download-student_${student.id}`}
                                label={student.fullName}
                                checked={values.students.includes(student.id.toString())}
                                onChange={() => {
                                  if (values.students.includes(student.id.toString())) {
                                    arrayHelpers.remove(values.students.indexOf(student.id.toString()));
                                  } else {
                                    arrayHelpers.push(student.id.toString());
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </SelectStudentsTableLayout>
                      )}
                    />
                  </div>
                  <div className={'font-bold text-xl mt-md'}>
                    {translate('pages.REPORTS.DOWNLOAD.MODAL.settings')}
                  </div>
                  <SensitiveDataToggle
                    toggled={!!showSensitiveData}
                    toggleHandler={(toggled) => () => setShowSensitiveData(toggled)}
                  />
                </div>
              </div>
              <ModalFooter>
                <CancelButtonModal
                  type={'reset'}
                  disabled={isValidating || isSubmitting}
                  onClick={() => setShowIndividualReports(false)}
                />
                <ConfirmButtonModal
                  type={'submit'}
                  icon={GLOBAL_ICONS.download}
                  disabled={!isValid || isValidating || isSubmitting}
                >
                  {translate('components.BUTTONS.DOWNLOAD.reports', {
                    count: values.students.length,
                    students: values.students.length,
                  })}
                </ConfirmButtonModal>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </Collapse>
    </>
  );
};

HandleDownloadModal.displayName = 'HandleDownloadModal';

export default HandleDownloadModal;
